import { useState, useEffect } from "react";
import { MainContent } from "./components/MainContent";
import { BottomNav } from "./components/BottomNav";
import { DragButton } from "./components/DragButton";
import type { WebsiteLink } from "./components/WebsiteGrid";

export type TabType = "location" | "categories" | "search" | "maps" | "about";

export default function App() {
  const [activeTab, setActiveTab] = useState<TabType>("maps");
  const [selectedMap, setSelectedMap] = useState<WebsiteLink | null>(null);
  const [hasLocationFilters, setHasLocationFilters] = useState(false);
  const [hasCategoryFilters, setHasCategoryFilters] = useState(false);
  const [hasSearchFilters, setHasSearchFilters] = useState(false);
  
  const handleCloseMenu = () => {
    setActiveTab("maps");
  };

  useEffect(() => {
    let scrolling = false;
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          if (window.scrollY >= 180 && !scrolling) {
            scrolling = true;
            const startY = window.scrollY;
            const targetY = 50;
            const startTime = performance.now();
            const duration = 600;
            
            const animateScroll = (currentTime: number) => {
              const elapsed = currentTime - startTime;
              const progress = Math.min(elapsed / duration, 1);
              
              const easeOut = 1 - Math.pow(1 - progress, 3);
              const newY = startY - (startY - targetY) * easeOut;
              
              window.scrollTo(0, newY);
              
              if (progress < 1) {
                requestAnimationFrame(animateScroll);
              } else {
                scrolling = false;
              }
            };
            
            requestAnimationFrame(animateScroll);
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-[calc(100dvh+200px)] w-full relative flex flex-col">
      {/* Fixed radial background glow - never scrolls */}
      <div 
        className="fixed inset-0 pointer-events-none z-[1]"
        style={{
          background: `radial-gradient(circle at 50% 50dvh, rgba(255, 255, 255, ${activeTab === 'search' ? '0.05' : '0.1'}) 0%, transparent 50%)`
        }}
        aria-hidden="true"
      />
      
      {/* Skip to navigation link for keyboard users */}
      <a
        href="#bottom-navigation"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:bg-white focus:text-black focus:px-4 focus:py-2 focus:rounded focus:shadow-lg"
      >
        Skip to navigation
      </a>
      
      <div className="flex-1 flex flex-col relative z-[2]">
        <MainContent 
          activeTab={activeTab} 
          switchToMapTab={handleCloseMenu}
          selectedMap={selectedMap}
          setSelectedMap={setSelectedMap}
          setHasLocationFilters={setHasLocationFilters}
          setHasCategoryFilters={setHasCategoryFilters}
          setHasSearchFilters={setHasSearchFilters}
        />
      </div>
      <BottomNav 
        isHidden={!!selectedMap}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        hasLocationFilters={hasLocationFilters}
        hasCategoryFilters={hasCategoryFilters}
        hasSearchFilters={hasSearchFilters}
      />
      <DragButton isHidden={!!selectedMap} />
    </div>
  );
}